import { Box, Stack, Typography } from "@mui/material";

import * as Yup from "yup";
import FormContainer from "../FormContainer";
import MuiTextField from "../inputs/MuiTextField";
import AnimateButton from "../extended/AnimateButton";
import Password from "../Password";
import useUser from "../../../hooks/useUser";
import useRateLimiter from "../../../hooks/useRateLimiter";
import SubmitButton from "../buttons/SubmitButton";
import { useSnackbarHelper } from "../Snackbar";

// ===============================|| JWT LOGIN ||=============================== //

const JWTLogin = () => {
  const { userLogin, isLoggingIn } = useUser();
  const showSnackbar = useSnackbarHelper();
  const { isLocked, remainingLockout, recordFailure, recordSuccess } =
    useRateLimiter("loginAttempts");

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email("Must be a valid email")
      .max(255)
      .required("Required")
      .matches(emailRegex, "Must be a valid email"),
    password: Yup.string().max(255).required("Required"),
  });

  type FormValues = Yup.InferType<typeof validationSchema>;

  const initialValues = {
    email: "",
    password: "",
  };

  const onFormSubmit = async (values: FormValues) => {
    if (isLocked) return;

    try {
      const payload = { ...values, isSite: true };
      const data = await userLogin(payload);

      recordSuccess();
      const message = data?.message || "Success";

      showSnackbar(message, "success");
    } catch (error: any) {
      recordFailure();
      const message = error?.response?.data?.message || "An error occurred";

      showSnackbar(message, "error");
    }
  };

  return (
    <FormContainer
      validation={validationSchema}
      defaultValues={initialValues}
      onSuccess={onFormSubmit}
    >
      <Stack sx={{ mb: 1 }} spacing={2}>
        <Box>
          <MuiTextField
            name="email"
            type="text"
            placeholder="Enter your email"
            required
            label="Email"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>

        <Box>
          <Password
            name="password"
            placeholder="Enter your password"
            label="Password"
            required
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
      </Stack>

      {isLocked && (
        <Typography
          variant="body2"
          sx={{ color: "error.main", textAlign: "center", mt: 1 }}
        >
          Too many failed attempts. Try again in {remainingLockout} seconds.
        </Typography>
      )}

      <Box sx={{ mt: 2 }}>
        <AnimateButton>
          <SubmitButton
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={isLoggingIn || isLocked}
          >
            {isLocked ? `Wait ${remainingLockout}s` : "Login"}
          </SubmitButton>
        </AnimateButton>
      </Box>
    </FormContainer>
  );
};

export default JWTLogin;
